import { useState } from 'react';
import classNames from 'classnames';

import styles from './DepositInput.module.scss';

import { EBaseTextWeight, EBaseTextSize } from '@spinwise/react-component-library/BaseText';
import { amountToCurrency, currencyToAmount } from 'Utils/index';
import { EBrandTextColor } from 'Libraries/typography.library';
import { t } from 'i18next';
import { ButtonLibrary } from 'Libraries/buttons.library';
import { useRegType } from 'Hooks/useRegType';
import { ButtonTextOnly } from '@spinwise/react-component-library/ButtonTextOnly';
import {
  EBaseButtonSize,
  EButtonBorderRadiusStrength,
  EButtonTextAlign,
} from '@spinwise/react-component-library/ButtonAbstract';
import { RegTypes } from 'Types/globals';
import { useSelector } from 'react-redux';
import { IApplicationState } from '@spinwise/spinwise-core';

interface DepositInputProps {
  selectedValue: number | string;
  minDepAmount: number;
  onAmountSelected: Function;
  onClickMobileInput: Function;
  playAnim: string;
  setPlayAnim: (value: string) => void;
  setBeforeFirstInput: (value: boolean) => void;
  setSelectedValue: Function;
  beforeFirstInput: boolean;
}

const IncrementValues = [10, 50, 100, 500, 1000];

const DepositInput = ({
  selectedValue,
  minDepAmount,
  onAmountSelected,
  onClickMobileInput,
  playAnim,
  setPlayAnim,
  setBeforeFirstInput,
  beforeFirstInput,
  setSelectedValue,
}: DepositInputProps) => {
  const [prevValue, setPrevValue] = useState(selectedValue);
  const def_currency = useSelector((state: IApplicationState) => state.globalReducer.geo.def_currency);

  const isRegType = useRegType();
  const emailRegType = isRegType(RegTypes.EMAIL);

  const tmpDisableInput = playAnim.length > 0;

  const adjustValue = ({ isIncrement }: { isIncrement: boolean }) => {
    if (tmpDisableInput) return;

    let newValue = Number(selectedValue || 0);

    if (newValue >= IncrementValues?.[4]) {
      newValue = isIncrement
        ? newValue + IncrementValues?.[4]
        : Math.max(newValue - IncrementValues?.[4], IncrementValues?.[4] - IncrementValues?.[2]);
    } else if (newValue >= IncrementValues?.[3]) {
      newValue = isIncrement
        ? newValue + IncrementValues?.[2]
        : Math.max(newValue - IncrementValues?.[2], IncrementValues?.[3] - IncrementValues?.[1]);
    } else if (newValue >= IncrementValues?.[2]) {
      newValue = isIncrement
        ? newValue + IncrementValues?.[1]
        : Math.max(newValue - IncrementValues?.[1], IncrementValues?.[2] - IncrementValues?.[0]);
    } else {
      newValue += isIncrement ? IncrementValues?.[0] : -IncrementValues?.[0];
    }

    if (isIncrement) {
      setSelectedValue(Math.max(Math.min(newValue, 10000), minDepAmount));
    } else {
      setSelectedValue(Math.max(newValue, minDepAmount));
    }

    // interacted, show new value
    setBeforeFirstInput(false);
  };

  return (
    <div className={classNames('d-fl jc-sb ai-c w-100', styles.deposit_container__sum__wrapper)}>
      {!emailRegType && (
        <div className={styles.button__wrapper}>
          <ButtonTextOnly
            alignText={EButtonTextAlign.Center}
            borderRadius={EButtonBorderRadiusStrength.Eight}
            size={EBaseButtonSize.Medium}
            type={ButtonLibrary.KeyboardButton}
            onClick={() => adjustValue({ isIncrement: false })}
            textWeight={EBaseTextWeight.W500}
            textSize={EBaseTextSize.Title4}
            textColor={EBrandTextColor.White06}
            fullWidth={true}
            label="-"
          />
        </div>
      )}
      <div onAnimationEnd={() => setPlayAnim('')} className={classNames(styles.deposit_container_sum_input, playAnim)}>
        <input
          className={styles.deposit_input_first}
          placeholder={selectedValue?.toString()}
          disabled={tmpDisableInput}
          type="text"
          value={beforeFirstInput ? t('dep.select.sum.enter.amount') : amountToCurrency(selectedValue, def_currency)}
          onFocus={() => {
            setPrevValue(selectedValue);
            if (beforeFirstInput) setBeforeFirstInput(false);
          }}
          onBlur={e => {
            if (currencyToAmount(e.target.value) === '') {
              onAmountSelected(prevValue?.toString());
            }
          }}
          onChange={e => onAmountSelected(currencyToAmount(e.target.value))}
          onClick={() => onAmountSelected('')}
        />
        <input
          className={styles.deposit_input_second}
          placeholder={selectedValue?.toString()}
          disabled={tmpDisableInput}
          type="text"
          value={amountToCurrency(minDepAmount, def_currency)}
          onFocus={() => setPrevValue(selectedValue)}
          onBlur={e => {
            if (e.target.value === '') {
              onAmountSelected(prevValue?.toString());
            }
          }}
          onChange={e => onAmountSelected(currencyToAmount(e.target.value))}
        />
        <div onClick={() => onClickMobileInput()}>
          <input
            disabled
            className={styles.deposit_input_mobile}
            value={beforeFirstInput ? t('dep.select.sum.enter.amount') : amountToCurrency(selectedValue, def_currency)}
          />
        </div>
        <div onClick={() => onClickMobileInput()}>
          <input disabled className={styles.deposit_input_mobile} value={minDepAmount} />
        </div>
      </div>
      {!emailRegType && (
        <div className={styles.button__wrapper}>
          <ButtonTextOnly
            alignText={EButtonTextAlign.Center}
            borderRadius={EButtonBorderRadiusStrength.Eight}
            size={EBaseButtonSize.Medium}
            type={ButtonLibrary.KeyboardButton}
            onClick={() => adjustValue({ isIncrement: true })}
            textWeight={EBaseTextWeight.W500}
            textSize={EBaseTextSize.Title4}
            textColor={EBrandTextColor.White06}
            fullWidth={true}
            label="+"
          />
        </div>
      )}
    </div>
  );
};

export default DepositInput;
